import { gql } from "@apollo/client";
import { CellEditRequestEvent } from "ag-grid-enterprise";
import { message } from "antd";
import { InfoFeatureType } from "change-events/change-event-polling";
import { SelectCellEditorV2 } from "components/cell-editor/select-cell-editor";
import {
  CompanyParticipationType,
  ErrorMessages,
  EUserTypes,
  UserParticipationType
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  SAVE_LOCATION_MATERIALS,
  UPDATE_FEATURE_BID_PACKAGE_BY_PK,
  UPDATE_FEATURE_COMPANY_PARTICIPANT,
  UPDATE_FEATURE_COMPANY_PARTICIPANT_WITH_EMPTY_USERS,
  UPDATE_FEATURE_INSTANCE_BY_PK,
  UPDATE_FEATURE_INSTANCE_SPEC_SECTION,
  UPDATE_FEATURE_MATERIALS,
  UPDATE_FEATURE_SHIPPING_LOCATION,
  UPDATE_FEATURE_SUBMITTALS
} from "entity-app/graphQL/ciq-feature-mutations";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { userEntityCellRenderer } from "entity-app/shared-components/log-render-components/cell-renders";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { useProjectParticipants } from "hooks/project-participants";
import { MeasurementSelectEditor } from "components/cell-editor/measurement-select-editor";
import { APPLY_WORKFLOW_TEMPLATE } from "pages/preconstruction-details/components/workflow-template-dropdown/query/mutation";
import { useContext } from "react";
import {
  isProjectInIntegrationMode,
  matchUserProjectRole,
  validatorForFieldValue
} from "utils/utils";
import { getCurrentDuration } from "entity-app/shared-components/entity-milestones-duration-columns";
import { getMilestoneBySequenceNo } from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import { isNaN } from "lodash";
import { DateUtils } from "utils/dateutils";
import { useUpdateUserParticipants } from "./update-user-participants";

const emptyAllowedFields: string[] = ["submittal_number", "manual_entry_date"];

export function useFeatureLogV2InlineEdit({
  refetchListData,
  infoFeatureType
}: {
  refetchListData: () => void;
  infoFeatureType: InfoFeatureType;
}) {
  const {
    gqlClientForProject,
    tokenRetrievalState,
    projectDetails,
    tokenContents
  } = useContext(ProjectContext);

  const [mutationUpdateFeatureInstanceFields] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCE_BY_PK),
    {
      client: gqlClientForProject
    }
  );

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  const [mutationUpdateFeatureBidPackageFields] = useCIQMutation(
    gql(UPDATE_FEATURE_BID_PACKAGE_BY_PK),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureMaterialFields] = useCIQMutation(
    gql(UPDATE_FEATURE_MATERIALS),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureCompanyParticipant] = useCIQMutation(
    gql(UPDATE_FEATURE_COMPANY_PARTICIPANT),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureCompanyParticipantWithEmptyUsers] =
    useCIQMutation(gql(UPDATE_FEATURE_COMPANY_PARTICIPANT_WITH_EMPTY_USERS), {
      client: gqlClientForProject
    });

  const [applyWorkflowTemplateMutation] = useCIQMutation(
    APPLY_WORKFLOW_TEMPLATE,
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureSpecSection] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCE_SPEC_SECTION),
    {
      client: gqlClientForProject
    }
  );

  const [mutationSaveLocationMaterials] = useCIQMutation(
    gql(SAVE_LOCATION_MATERIALS),
    {
      client: gqlClientForProject
    }
  );
  const [mutationUpdateFeatureShippingLocation] = useCIQMutation(
    gql(UPDATE_FEATURE_SHIPPING_LOCATION),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureSubmittalFields] = useCIQMutation(
    gql(UPDATE_FEATURE_SUBMITTALS),
    {
      client: gqlClientForProject
    }
  );

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const isSubmittalIntgMode =
    infoFeatureType === InfoFeatureType.submittal && isIntegrationMode;

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const { projectParticipants: WFProjectParticipants } = useProjectParticipants(
    isSubmittalIntgMode || !isLoggedInUserGC
  );

  const { updateUserAndCompany, updateFeatureUserParticipant } =
    useUpdateUserParticipants(WFProjectParticipants);

  const showMessage = async (updateResponse: any, revertDataFunc: Function) => {
    if (updateResponse.success) {
      message.success("Updated successfully.");
      refetchListData();
    } else {
      message.error("Failed to update.");
      revertDataFunc();
    }
  };

  const updateFeatureCompanyParticipantWithEmptyUsers = async ({
    featureInstanceId,
    companyParticipationType,
    userParticipationTypeId,
    vendorIds
  }: {
    featureInstanceId: string;
    companyParticipationType: CompanyParticipationType;
    userParticipationTypeId: UserParticipationType;
    vendorIds: Array<string>;
  }) => {
    const objects = vendorIds.map((id) => ({
      instance_id: featureInstanceId,
      company_participation_type_id: companyParticipationType,
      vendor_id: id
    }));
    const variables: any = {
      instance_id: featureInstanceId,
      company_participation_type_id: companyParticipationType,
      user_participation_type_id: userParticipationTypeId,
      objects
    };
    return mutationUpdateFeatureCompanyParticipantWithEmptyUsers({
      variables
    });
  };

  const updateFeatureCompanyParticipant = async ({
    featureInstanceId,
    companyParticipationType,
    vendorIds
  }: {
    featureInstanceId: string;
    companyParticipationType: CompanyParticipationType;
    vendorIds: Array<string>;
  }) => {
    const objects = vendorIds.map((id) => ({
      instance_id: featureInstanceId,
      company_participation_type_id: companyParticipationType,
      vendor_id: id
    }));
    const variables: any = {
      instance_id: featureInstanceId,
      company_participation_type_id: companyParticipationType,
      objects
    };
    return mutationUpdateFeatureCompanyParticipant({
      variables
    });
  };

  const onCellEditRequest = async (event: CellEditRequestEvent<any, any>) => {
    if (
      !emptyAllowedFields.includes(event.colDef.colId!) &&
      (event.newValue === "" || event.newValue === null)
    )
      return;

    const colId = event.colDef.colId!;
    const fieldId = event.colDef.field || "field";
    const existingRowData = { ...event.data };

    const revertDataFunc = () => {
      event.node.setData({ ...existingRowData });
    };

    let changedData: any = {};
    if (event.colDef.cellRenderer === userEntityCellRenderer) {
      changedData = event.newValue;
    } else if (event.colDef.cellEditor === MeasurementSelectEditor) {
      /// Nothing
    } else if (event.colDef.cellEditor === SelectCellEditorV2) {
      changedData[colId] = {
        id: event.newValue?.value || null,
        value: event.newValue?.label
      };
    } else {
      changedData[colId] = event.newValue;
      if (colId !== fieldId) changedData[fieldId] = event.newValue;
    }

    event.node.setData({ ...existingRowData, ...changedData });

    // Check if a colId matches the pattern du2_1, du2_2, etc. ( bid and design log pages)
    if (/^du2_\d+$/.test(colId)) {
      // This is a duration column
      const index = parseInt(colId.split("_")[1], 10); // Extract the number
      const dataWithMilestonesAndOffsets = structuredClone(event.data);
      const currentDuration = getCurrentDuration(
        dataWithMilestonesAndOffsets,
        index
      );

      const newDuration = Number(event.newValue);

      if (currentDuration && !isNaN(newDuration) && newDuration >= 0) {
        currentDuration.duration = event.newValue;
        event.node.setData(dataWithMilestonesAndOffsets);

        const variables: any = {
          input: [
            {
              feature_instance_id: currentDuration.feature_instance_id,
              workflow_instance_id: currentDuration.workflow_instance_id,
              offsets: [
                {
                  id: currentDuration.id,
                  duration: event.newValue
                }
              ]
            }
          ],
          metadata: {
            request_source: "INLINE_EDIT_LOG_PAGE"
          }
        };
        const updateResponse = await applyWorkflowMutation({
          variables
        });

        showMessage(updateResponse, revertDataFunc);
        return;
      }
    }

    // Check if a colId matches the pattern offset_duration_1, offset_duration_2, etc. ( material and submittal log pages)
    if (/^offset_duration_\d+$/.test(colId)) {
      // This is a duration column
      const sequenceNo = parseInt(colId.split("_")[2], 10); // Extract the number
      const dataWithMilestonesAndOffsets = structuredClone(event.data);
      const currentDuration = getMilestoneBySequenceNo(
        { data: dataWithMilestonesAndOffsets },
        sequenceNo
      );
      const newDuration = Number(event.newValue);

      if (currentDuration && !isNaN(newDuration) && newDuration >= 0) {
        currentDuration.offset_duration = newDuration;
        event.node.setData(dataWithMilestonesAndOffsets);

        const variables: any = {
          input: [
            {
              feature_instance_id: event.data.id,
              workflow_instance_id:
                event.data?.date_block?.id ||
                event.data.feature_workflow_instance_id,
              offsets: [
                {
                  id: currentDuration.offset_id,
                  duration: event.newValue
                }
              ]
            }
          ],
          metadata: {
            request_source: "INLINE_EDIT_LOG_PAGE"
          }
        };
        const updateResponse = await applyWorkflowMutation({
          variables
        });

        showMessage(updateResponse, revertDataFunc);
        return;
      }
    }

    switch (colId) {
      case "number":
      case "submittal_number":
      case "name":
      case "title":
        {
          if (
            ["number", "name"].includes(colId) &&
            event.newValue === event.oldValue
          ) {
            return;
          }
          if (event?.newValue?.trim() === event?.oldValue?.trim()) {
            return;
          }
          const getUniqueError = () => {
            switch (infoFeatureType) {
              case InfoFeatureType.designPackage:
                if (colId === "number")
                  return ErrorMessages.DesignPackageNumberMsg;
                return ErrorMessages.DesignPackageTitleMsg;
              case InfoFeatureType.bidPackage:
                if (colId === "number")
                  return ErrorMessages.BidPackageNumberMsg;
                return ErrorMessages.BidPackageTitleMsg;
              case InfoFeatureType.material:
                return ErrorMessages.MaterialExists;
              default:
                return ErrorMessages.SomethingWentWrong;
            }
          };
          try {
            await validatorForFieldValue(
              event.colDef.field!,
              event.newValue?.trim(),
              infoFeatureType,
              tokenRetrievalState?.token,
              getUniqueError()
            );
          } catch (err) {
            message.error(err);
            revertDataFunc();
            return;
          }

          const variables: any = {
            featureId: event.data.id,
            set: { [event.colDef.field!]: event.newValue }
          };
          const updateResponse = await mutationUpdateFeatureInstanceFields({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "wave":
      case "no_of_bidders":
      case "estimated_value":
      case "awarded_value":
        {
          const variables: any = {
            featureId: event.data.id,
            set: { [event.colDef.field!]: event.newValue }
          };
          const updateResponse = await mutationUpdateFeatureBidPackageFields({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "planned_float":
      case "pre_WF_start_float":
        {
          const input = [
            {
              feature_instance_id: event.data.id,
              workflow_instance_id:
                event.data?.date_block?.id ||
                event.data.feature_workflow_instance_id,
              pre_WF_start_float: event.newValue
            }
          ];

          const updateResponse = await applyWorkflowMutation({
            variables: {
              input,
              metadata: {
                request_source: "INLINE_EDIT_LOG_PAGE"
              }
            }
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "manual_entry_date":
        {
          const input = [
            {
              feature_instance_id: event.data.id,
              workflow_instance_id:
                event.data?.date_block?.id ||
                event.data.feature_workflow_instance_id,
              manual_entry_date: event.newValue
                ? DateUtils.formatDateWithLunchTime(event.newValue)
                : null
            }
          ];

          const updateResponse = await applyWorkflowMutation({
            variables: {
              input,
              metadata: {
                request_source: "INLINE_EDIT_LOG_PAGE"
              }
            }
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "bidding_subcontractors":
        {
          const vendorIds = event.newValue.map((x: any) => x.id);
          const updateResponse = await updateFeatureCompanyParticipant({
            featureInstanceId: event.data.id,
            companyParticipationType:
              CompanyParticipationType.BIDDING_SUB_CONTRACTOR,
            vendorIds
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "bid_scope":
        {
          const objects = event.newValue.map((x: any) => ({
            bid_scope: true,
            feature_instance_id: event.data.id,
            spec_section_id: x.id
          }));

          const variables: any = {
            instance_id: event.data.id,
            objects
          };
          const updateResponse = await mutationUpdateFeatureSpecSection({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "custom_spec_section":
        {
          const objects = event.newValue.map((op: any) => ({
            bid_scope: false,
            feature_instance_id: event.data.id,
            spec_section_id: op.id
          }));

          const variables: any = {
            instance_id: event.data.id,
            objects
          };
          const updateResponse = await mutationUpdateFeatureSpecSection({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "preconstruction_lead_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.preconstruction_lead_id,
            userParticipationType: UserParticipationType.PRECONSTRUCTION_LEAD
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "gc_representative":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.gc_representative?.id,
            userParticipationType: UserParticipationType.GC_REPRESENTATIVE
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "gc_representative_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.gc_representative_id,
            userParticipationType: UserParticipationType.GC_REPRESENTATIVE
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "operations_lead_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.operations_lead_id,
            userParticipationType: UserParticipationType.OPERATIONS_LEAD
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "trade_partner":
        {
          const updateResponse =
            await updateFeatureCompanyParticipantWithEmptyUsers({
              featureInstanceId: event.data.id,
              companyParticipationType:
                CompanyParticipationType.RESPONSIBLE_CONTRACTOR,
              vendorIds: [event.newValue.responsible_contractor?.id],
              userParticipationTypeId: UserParticipationType.ASSIGNEE
            });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "responsible_contractor":
        {
          const updateResponse =
            await updateFeatureCompanyParticipantWithEmptyUsers({
              featureInstanceId: event.data.id,
              companyParticipationType:
                CompanyParticipationType.RESPONSIBLE_CONTRACTOR,
              vendorIds: [event.newValue.responsible_contractor?.id],
              userParticipationTypeId: UserParticipationType.SUBMITTER
            });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "design_firm_id":
        {
          const updateResponse =
            await updateFeatureCompanyParticipantWithEmptyUsers({
              featureInstanceId: event.data.id,
              companyParticipationType:
                CompanyParticipationType.DESIGN_FIRM_CONTRACTOR,
              vendorIds: [event.newValue.design_firm_id],
              userParticipationTypeId:
                UserParticipationType.RESPONSIBLE_DESIGNER
            });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "responsible_designer_id":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.responsible_designer_id,
            userParticipationType: UserParticipationType.RESPONSIBLE_DESIGNER
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "assignee":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.assignee_user.id,
            userParticipationType: UserParticipationType.ASSIGNEE
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "awarded_subcontractor_id":
        {
          const updateResponse = await updateFeatureCompanyParticipant({
            featureInstanceId: event.data.id,
            companyParticipationType:
              CompanyParticipationType.AWARDED_SUB_CONTRACTOR,
            vendorIds: [event.newValue.awarded_subcontractor_id]
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "bid_package_template_id":
        {
          const variables = {
            input: [
              {
                feature_instance_id: event.data.id,
                workflow_instance_id: event.data.feature_workflow_instance_id,
                workflow_template_id: event.newValue.bid_package_template_id
              }
            ],
            metadata: {
              request_source: "INLINE_EDIT_LOG_PAGE"
            }
          };

          const updateResponse = await applyWorkflowTemplateMutation({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "date_block_template_name":
        {
          const variables = {
            input: [
              {
                feature_instance_id: event.data.id,
                workflow_instance_id: event.data.date_block?.id,
                workflow_template_id: event.newValue.template.id
              }
            ],
            metadata: {
              request_source: "INLINE_EDIT_LOG_PAGE"
            }
          };

          const updateResponse = await applyWorkflowTemplateMutation({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "purchase_order":
      case "material_tag":
      case "manufacturer":
      case "part_number":
      case "serial_number":
      case "warranty":
        {
          const variables: any = {
            featureId: event.data.id,
            set: {
              [event.colDef.field!]: event.newValue ?? null
            }
          };
          const updateResponse = await mutationUpdateFeatureMaterialFields({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "material_size_unit":
      case "quantity_unit":
        {
          const variables: any = {
            featureId: event.data.id,
            set: {}
          };

          variables.set = {
            [event.newValue.unitValue.prop]: event.newValue.unitValue.value,
            [event.newValue.unitType.prop]: event.newValue.unitType.value
          };

          changedData[event.newValue.unitValue.prop] =
            event.newValue.unitValue.value;

          changedData[event.newValue.unitType.name] = event.newValue.label;

          event.node.setData({ ...existingRowData, ...changedData });

          const updateResponse = await mutationUpdateFeatureMaterialFields({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "locations":
        {
          const variables = {
            feature_instance_ids: [event.data.id],
            feature_type: infoFeatureType,
            location_ids: event.newValue.map((x: any) => x.id)
          };
          const updateResponse = await mutationSaveLocationMaterials({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "shipping_location":
        {
          const variables = {
            featureIds: [event.data.id],
            placeId: event.newValue.value
          };
          const updateResponse = await mutationUpdateFeatureShippingLocation({
            variables
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;

      case "type":
        {
          const variables: any = {
            featureId: event.data.id,
            set: {
              [event.colDef.colId!]: event.newValue?.value || null,
              raw_type: event.newValue?.label || ""
            }
          };
          const updateResponse = await mutationUpdateFeatureSubmittalFields({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "gc_reviewer":
        {
          const updateResponse = await updateFeatureUserParticipant({
            featureInstanceId: event.data.id,
            userId: event.newValue.gc_user?.id,
            userParticipationType: UserParticipationType.GC_REVIEWER
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "design_reviewer":
        {
          const mutationUpdateResponse = await updateUserAndCompany({
            userId: event.newValue.design_reviewer?.id,
            featureInstanceId: event.data.id,
            projectParticipants: WFProjectParticipants.designReviewers
          });

          showMessage(mutationUpdateResponse, revertDataFunc);
        }
        break;
      case "submitter":
        {
          const updateResponse = await updateFeatureUserParticipant({
            userId: event.newValue.submitter_reviewer?.id,
            featureInstanceId: event.data.id,
            userParticipationType: UserParticipationType.SUBMITTER
          });

          showMessage(updateResponse, revertDataFunc);
        }
        break;
      case "watchers":
      case "priority":
        {
          const variables: any = {
            featureId: event.data.id,
            set: { [event.colDef.colId!]: event.newValue?.value || null }
          };
          const updateResponse = await mutationUpdateFeatureSubmittalFields({
            variables
          });
          showMessage(updateResponse, revertDataFunc);
        }
        break;

      default:
        break;
    }
  };

  return { onCellEditRequest };
}
